import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Truck, User, Upload, AlertCircle, CheckCircle2, FileText, Plus, Search, Database, Users, Trash2, RefreshCw, Loader2, Phone, Handshake, Building2, UserCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { VEHICLE_TYPES, VEHICLE_CATEGORIES, getVehicleTypeLabel, getVehicleTypeIcon } from '../constants/vehicleTypes';
import VehicleSelectorModal from './VehicleSelectorModal';
import { useAuth } from '../contexts/AuthContext';

const token = 'cookie-auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PARTNER_TYPE_LABELS = {
  'entreprise': 'Entreprise',
  'Entreprise ou societe de transport': 'Entreprise',
  'independant': 'Indépendant',
  'Personne morale': 'Indépendant',
  'chauffeur_proprietaire': 'Chauffeur-proprio',
  'Personne physique': 'Chauffeur-proprio',
};

const PARTNER_TYPE_ICONS = {
  'entreprise': Building2,
  'Entreprise ou societe de transport': Building2,
  'independant': Truck,
  'Personne morale': Truck,
  'chauffeur_proprietaire': UserCircle,
  'Personne physique': UserCircle,
};

const REQUIRED_DOCUMENTS = [
  { type: 'carte_grise', label: 'Carte grise du véhicule', icon: FileText },
  { type: 'cni_chauffeur', label: 'CNI du chauffeur', icon: FileText },
  { type: 'permis_conduire', label: 'Permis de conduire', icon: FileText },
  { type: 'assurance_camion', label: 'Assurance du camion', icon: FileText },
  { type: 'visite_technique', label: 'Visite technique', icon: FileText },
];

const PlanificationModal = ({ open, onClose, operation, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0); // 0: Partner, 1: Drivers, 2: Vehicles, 3: Documents
  
  // Partner selection
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerSearchResults, setPartnerSearchResults] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [loadingPartners, setLoadingPartners] = useState(false);
  
  // Number of trucks requested by client
  const trucksRequested = operation?.numberOfTrucks || 1;
  
  // Available resources - LOCAL
  const [localDrivers, setLocalDrivers] = useState([]);
  const [localVehicles, setLocalVehicles] = useState([]);
  
  // Available resources - EXTERNAL (Truck DB)
  const [externalDrivers, setExternalDrivers] = useState([]);
  const [externalVehicles, setExternalVehicles] = useState([]);
  const [loadingExternal, setLoadingExternal] = useState(false);
  
  // Selected assignments (array for multi-truck support)
  const [assignments, setAssignments] = useState([]);
  
  // Search query
  const [driverSearch, setDriverSearch] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  
  // Source filter
  const [driverSource, setDriverSource] = useState('all'); // 'all', 'local', 'external'
  const [vehicleSource, setVehicleSource] = useState('all');
  
  // Documents
  const [uploadedDocuments, setUploadedDocuments] = useState({});
  const [documentFiles, setDocumentFiles] = useState({});
  
  // Vehicle Selector Modal
  const [showVehicleSelector, setShowVehicleSelector] = useState(false);
  const [currentAssignmentIndex, setCurrentAssignmentIndex] = useState(0);

  // Track if modal was just opened (to avoid re-init on operation refresh)
  const [hasInitialized, setHasInitialized] = useState(false);

  // Reset form when modal opens (only once per open)
  useEffect(() => {
    if (open && operation && !hasInitialized) {
      setStep(0);
      setDriverSearch('');
      setVehicleSearch('');
      setSelectedPartner(null);
      setPartnerSearch('');
      setPartnerSearchResults([]);
      
      // Initialize assignments based on numberOfTrucks
      const numTrucks = operation?.numberOfTrucks || 1;
      
      // Check if operation already has assignments (for re-planning)
      const existingAssignments = operation?.assignments || [];
      
      if (existingAssignments.length > 0) {
        // Load existing assignments
        const loadedAssignments = existingAssignments.map((a, i) => ({
          index: i,
          driver: a.driverId ? {
            id: a.driverId,
            firstName: a.driverName?.split(' ')[0] || '',
            lastName: a.driverName?.split(' ').slice(1).join(' ') || '',
            phone: a.driverPhone,
            source: a.driverSource || 'local'
          } : null,
          vehicle: a.vehicleId ? {
            id: a.vehicleId,
            registration: a.vehiclePlate,
            vehicleType: a.vehicleType,
            brand: a.vehicleBrand,
            model: a.vehicleModel,
            source: a.vehicleSource || 'local'
          } : null,
          isManualDriver: a.isManualDriver || false,
          isManualVehicle: a.isManualVehicle || false,
          manualDriverName: a.isManualDriver ? a.driverName : '',
          manualDriverPhone: a.isManualDriver ? a.driverPhone : '',
          manualVehiclePlate: a.isManualVehicle ? a.vehiclePlate : '',
          manualVehicleType: a.vehicleType || operation?.vehicleType || '',
          manualVehicleBrand: a.isManualVehicle ? a.vehicleBrand : '',
          manualVehicleModel: a.isManualVehicle ? a.vehicleModel : '',
          manualVehicleCapacity: a.isManualVehicle ? a.vehicleCapacity : '',
          hasTrailer: a.hasTrailer || false,
          trailerPlate: a.trailerPlate || ''
        }));
        
        // Add empty slots if more trucks requested than assigned
        while (loadedAssignments.length < numTrucks) {
          loadedAssignments.push({
            index: loadedAssignments.length,
            driver: null,
            vehicle: null,
            isManualDriver: false,
            isManualVehicle: false,
            manualDriverName: '',
            manualDriverPhone: '',
            contactPhone: user?.phone || '',
            manualVehiclePlate: '',
            manualVehicleType: operation?.vehicleType || '',
            manualVehicleBrand: '',
            manualVehicleModel: '',
            manualVehicleCapacity: '',
            hasTrailer: false,
            trailerPlate: ''
          });
        }
        
        setAssignments(loadedAssignments);
        toast({
          title: "Données de planification chargées",
          description: `${existingAssignments.length} assignation(s) existante(s) récupérée(s)`,
        });
      } else if (operation?.assignedDriver || operation?.assignedVehicle) {
        // Legacy single driver/vehicle - convert to assignments format
        const legacyAssignment = {
          index: 0,
          driver: operation.assignedDriver?.name ? {
            id: operation.assignedDriver?.id,
            firstName: operation.assignedDriver?.firstName || operation.assignedDriver?.name?.split(' ')[0] || '',
            lastName: operation.assignedDriver?.lastName || operation.assignedDriver?.name?.split(' ').slice(1).join(' ') || '',
            phone: operation.assignedDriver?.phone || operation.assignedDriverPhone,
            source: 'local'
          } : null,
          vehicle: operation.assignedVehicle?.plate ? {
            id: operation.assignedVehicle?.plate,
            registration: operation.assignedVehicle?.plate,
            vehicleType: operation.assignedVehicle?.type || operation.vehicleType,
            source: 'local'
          } : null,
          isManualDriver: !operation.assignedDriver?.id,
          isManualVehicle: true, // Assume manual since we have plate but no ID,
          manualDriverName: !operation.assignedDriver?.id ? operation.assignedDriver?.name : '',
          manualDriverPhone: !operation.assignedDriver?.id ? operation.assignedDriver?.phone : '',
          manualVehiclePlate: operation.assignedVehicle?.plate || '',
          manualVehicleType: operation.assignedVehicle?.type || operation?.vehicleType || '',
          manualVehicleBrand: '',
          manualVehicleModel: '',
          manualVehicleCapacity: '',
          hasTrailer: false,
          trailerPlate: ''
        };
        
        const initialAssignments = [legacyAssignment];
        
        // Add empty slots for remaining trucks
        for (let i = 1; i < numTrucks; i++) {
          initialAssignments.push({
            index: i,
            driver: null,
            vehicle: null,
            isManualDriver: false,
            isManualVehicle: false,
            manualDriverName: '',
            manualDriverPhone: '',
            contactPhone: user?.phone || '',
            manualVehiclePlate: '',
            manualVehicleType: operation?.vehicleType || '',
            manualVehicleBrand: '',
            manualVehicleModel: '',
            manualVehicleCapacity: '',
            hasTrailer: false,
            trailerPlate: ''
          });
        }
        
        setAssignments(initialAssignments);
        toast({
          title: "Données existantes chargées",
          description: "Les informations de planification précédente ont été récupérées",
        });
      } else {
        // No existing data - initialize empty assignments
        const initialAssignments = Array.from({ length: numTrucks }, (_, i) => ({
          index: i,
          driver: null,
          vehicle: null,
          isManualDriver: false,
          isManualVehicle: false,
          manualDriverName: '',
          manualDriverPhone: '',
          contactPhone: user?.phone || '',
          manualVehiclePlate: '',
          manualVehicleType: operation?.vehicleType || '',
          manualVehicleBrand: '',
          manualVehicleModel: '',
          manualVehicleCapacity: '',
          hasTrailer: false,
          trailerPlate: ''
        }));
        setAssignments(initialAssignments);
      }
      
      setHasInitialized(true);
      fetchAllResources();
      
      // Directly fetch partners inline
      (async () => {
        try {
          if (!token) return;
          const headers = { 'Authorization': 'Bearer cookie-auth' };
          // Don't pre-filter by vehicle_type on initial load - show all available partners
          const params = new URLSearchParams({ limit: '20' });
          const url = `${API}/transport-partners/search-for-assignment?${params}`;
          const res = await axios.get(url, { headers });
          setPartnerSearchResults(res.data.partners || []);
        } catch (e) {
          console.error('[PlanificationModal] Init partner fetch error:', e.message);
        }
      })();
    }
    
    // Reset hasInitialized when modal closes
    if (!open) {
      setHasInitialized(false);
    }
  }, [open, operation]);

  const fetchAllResources = async () => {
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      
      // Fetch local resources
      const [localDriversRes, localVehiclesRes] = await Promise.all([
        axios.get(`${API}/drivers?available_only=true`, { headers }),
        axios.get(`${API}/vehicles?available_only=true`, { headers })
      ]);
      
      setLocalDrivers((localDriversRes.data || []).map(d => ({ ...d, source: 'local' })));
      setLocalVehicles((localVehiclesRes.data || []).map(v => ({ ...v, source: 'local' })));
      
      // Fetch external resources (Truck DB)
      fetchExternalResources();
      
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const fetchExternalResources = async () => {
    setLoadingExternal(true);
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      
      const [driversRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/external/drivers?limit=200&available_only=true`, { headers }).catch(() => ({ data: { drivers: [] } })),
        axios.get(`${API}/external/vehicles?limit=200&available_only=true`, { headers }).catch(() => ({ data: { vehicles: [] } }))
      ]);
      
      const extDrivers = (driversRes.data.drivers || []).map(d => ({
        id: d.id,
        firstName: d.nom?.split(' ')[0] || d.nom || '',
        lastName: d.nom?.split(' ').slice(1).join(' ') || '',
        phone: d.telephone || '',
        matricule: d.matricule,
        status: d.disponible ? 'disponible' : 'occupe',
        source: 'external'
      }));
      
      const extVehicles = (vehiclesRes.data.vehicles || []).map(v => ({
        id: v.id,
        registration: v.immatriculation || v.plate,
        vehicleType: v.type_camion || v.type,
        capacity: v.capacite_tonnes || v.capacity,
        brand: v.marque,
        model: v.modele,
        source: 'external'
      }));
      
      setExternalDrivers(extDrivers);
      setExternalVehicles(extVehicles);
      
    } catch (error) {
      console.error('Error fetching external resources:', error);
    } finally {
      setLoadingExternal(false);
    }
  };

  // Fetch partners for assignment
  const fetchPartners = async (searchText = '') => {
    setLoadingPartners(true);
    try {
      if (!token) return;
      const headers = { 'Authorization': 'Bearer cookie-auth' };
      const params = new URLSearchParams({ limit: '20' });
      if (searchText) params.append('search', searchText);
      
      const res = await axios.get(`${API}/transport-partners/search-for-assignment?${params}`, { headers });
      setPartnerSearchResults(res.data.partners || []);
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartnerSearchResults([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  // Debounced partner search on typing
  useEffect(() => {
    if (step !== 0 || !open || !hasInitialized) return;
    const timer = setTimeout(() => fetchPartners(partnerSearch), partnerSearchResults.length === 0 && !partnerSearch ? 0 : 400);
    return () => clearTimeout(timer);
  }, [partnerSearch, step, open, hasInitialized]);

  // Select a partner and pre-fill assignments if chauffeur-proprietaire
  const handleSelectPartner = (partner) => {
    setSelectedPartner(partner);
    // Lot 1 : par défaut on ne propose QUE la flotte du transporteur choisi
    setDriverSource('partner');
    setVehicleSource('partner');
    
    // For chauffeur-proprietaire: auto-fill driver with the owner (isSelf)
    const pType = partner.type || '';
    const isChauffeurProprio = pType === 'chauffeur_proprietaire' || pType === 'Personne physique';
    
    if (isChauffeurProprio) {
      const selfDriver = (partner.drivers || []).find(d => d.isSelf);
      if (selfDriver) {
        setAssignments(prev => prev.map((a, i) => i === 0 ? {
          ...a,
          driver: {
            id: selfDriver.id,
            firstName: selfDriver.name?.split(' ')[0] || '',
            lastName: selfDriver.name?.split(' ').slice(1).join(' ') || '',
            phone: selfDriver.phone,
            source: 'partner'
          },
          isManualDriver: false
        } : a));
      } else {
        // Use partner contact info as the driver
        setAssignments(prev => prev.map((a, i) => i === 0 ? {
          ...a,
          isManualDriver: true,
          manualDriverName: partner.contactName || partner.name,
          manualDriverPhone: partner.phone,
          driver: null
        } : a));
      }
    }
    
    toast({
      title: `Partenaire sélectionné`,
      description: `${partner.name} (${PARTNER_TYPE_LABELS[partner.type] || partner.type})`,
    });
  };

  // Get filtered drivers based on search and source
  const getFilteredDrivers = () => {
    let drivers = [];
    
    if (driverSource === 'partner' && selectedPartner) {
      drivers = (selectedPartner.drivers || []).map(d => ({
        id: d.id,
        firstName: d.name?.split(' ')[0] || '',
        lastName: d.name?.split(' ').slice(1).join(' ') || '',
        phone: d.phone,
        status: d.status || 'disponible',
        isSelf: d.isSelf,
        source: 'partner'
      }));
    } else {
      if (driverSource === 'all' || driverSource === 'local') {
        drivers = [...drivers, ...localDrivers];
      }
      if (driverSource === 'all' || driverSource === 'external') {
        drivers = [...drivers, ...externalDrivers];
      }
      // Also include partner drivers in 'all' mode if partner is selected
      if (driverSource === 'all' && selectedPartner) {
        const partnerDrivers = (selectedPartner.drivers || []).map(d => ({
          id: d.id,
          firstName: d.name?.split(' ')[0] || '',
          lastName: d.name?.split(' ').slice(1).join(' ') || '',
          phone: d.phone,
          status: d.status || 'disponible',
          isSelf: d.isSelf,
          source: 'partner'
        }));
        drivers = [...partnerDrivers, ...drivers];
      }
    }
    
    if (driverSearch) {
      const search = driverSearch.toLowerCase();
      drivers = drivers.filter(d => {
        const name = `${d.firstName || ''} ${d.lastName || ''} ${d.nom || ''}`.toLowerCase();
        const phone = d.phone || d.telephone || '';
        const matricule = d.matricule || '';
        return name.includes(search) || phone.includes(search) || matricule.toLowerCase().includes(search);
      });
    }
    
    // Exclude already selected drivers
    const selectedDriverIds = assignments.filter(a => a.driver).map(a => a.driver.id);
    return drivers.filter(d => !selectedDriverIds.includes(d.id));
  };

  // Get filtered vehicles based on search and source
  const getFilteredVehicles = () => {
    let vehicles = [];
    
    if (vehicleSource === 'partner' && selectedPartner) {
      vehicles = (selectedPartner.vehicles || []).map(v => ({
        id: v.id,
        registration: v.plate,
        vehicleType: v.vehicleType,
        capacity: v.capacityTons,
        brand: v.brand,
        model: v.model,
        hasTrailer: v.hasTrailer || false,
        trailerPlate: v.trailerPlate || '',
        status: v.status || 'disponible',
        source: 'partner'
      }));
    } else {
      if (vehicleSource === 'all' || vehicleSource === 'local') {
        vehicles = [...vehicles, ...localVehicles];
      }
      if (vehicleSource === 'all' || vehicleSource === 'external') {
        vehicles = [...vehicles, ...externalVehicles];
      }
      // Also include partner vehicles in 'all' mode if partner is selected
      if (vehicleSource === 'all' && selectedPartner) {
        const partnerVehicles = (selectedPartner.vehicles || []).map(v => ({
          id: v.id,
          registration: v.plate,
          vehicleType: v.vehicleType,
          capacity: v.capacityTons,
          brand: v.brand,
          model: v.model,
          hasTrailer: v.hasTrailer || false,
          trailerPlate: v.trailerPlate || '',
          status: v.status || 'disponible',
          source: 'partner'
        }));
        vehicles = [...partnerVehicles, ...vehicles];
      }
    }
    
    if (vehicleSearch) {
      const search = vehicleSearch.toLowerCase();
      vehicles = vehicles.filter(v => {
        const plate = v.registration || v.plate || '';
        const type = v.vehicleType || v.type || '';
        return plate.toLowerCase().includes(search) || type.toLowerCase().includes(search);
      });
    }
    
    // Exclude already selected vehicles
    const selectedVehicleIds = assignments.filter(a => a.vehicle).map(a => a.vehicle.id);
    return vehicles.filter(v => !selectedVehicleIds.includes(v.id));
  };

  // Select a driver for an assignment
  const selectDriver = (assignmentIndex, driver) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, driver, isManualDriver: false } : a
    ));
  };

  // Remove driver from assignment
  const removeDriver = (assignmentIndex) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, driver: null } : a
    ));
  };

  // Select a vehicle for an assignment
  const selectVehicle = (assignmentIndex, vehicle) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { 
        ...a, 
        vehicle, 
        isManualVehicle: false,
        manualVehiclePlate: vehicle.registration || vehicle.plate || '',
        manualVehicleType: vehicle.vehicleType || vehicle.type || '',
        manualVehicleBrand: vehicle.brand || '',
        manualVehicleModel: vehicle.model || '',
        manualVehicleCapacity: vehicle.capacity?.toString() || '',
        hasTrailer: vehicle.hasTrailer || a.hasTrailer || false,
        trailerPlate: vehicle.trailerPlate || a.trailerPlate || ''
      } : a
    ));
  };

  // Remove vehicle from assignment
  const removeVehicle = (assignmentIndex) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, vehicle: null, manualVehiclePlate: '' } : a
    ));
  };

  // Toggle manual entry mode
  const toggleManualDriver = (assignmentIndex, checked) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, isManualDriver: checked, driver: checked ? null : a.driver } : a
    ));
  };

  const toggleManualVehicle = (assignmentIndex, checked) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, isManualVehicle: checked, vehicle: checked ? null : a.vehicle } : a
    ));
  };

  // Update manual entry fields
  const updateAssignment = (assignmentIndex, field, value) => {
    setAssignments(prev => prev.map((a, i) => 
      i === assignmentIndex ? { ...a, [field]: value } : a
    ));
  };

  const handleFileChange = (docType, file) => {
    if (file && file.size > 10 * 1024 * 1024) {
      toast({ title: "Fichier trop volumineux", description: "La taille maximale est de 10MB", variant: "destructive" });
      return;
    }
    setDocumentFiles(prev => ({ ...prev, [docType]: file }));
    setUploadedDocuments(prev => ({ ...prev, [docType]: file ? file.name : null }));
  };

  const validateStep1 = () => {
    // At least one assignment must have a driver
    const hasDriver = assignments.some(a => a.driver || (a.isManualDriver && a.manualDriverName && a.manualDriverPhone));
    if (!hasDriver) {
      toast({ title: "Chauffeur requis", description: "Veuillez sélectionner au moins un chauffeur", variant: "destructive" });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // At least one assignment must have a vehicle
    const hasVehicle = assignments.some(a => a.vehicle || (a.isManualVehicle && a.manualVehiclePlate));
    if (!hasVehicle) {
      toast({ title: "Véhicule requis", description: "Veuillez sélectionner au moins un véhicule", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 0) setStep(1); // Partner → Drivers (no validation needed, partner is optional)
    else if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const headers = { 'Authorization': 'Bearer cookie-auth', 'Content-Type': 'application/json' };

      // Build logistics data from assignments
      const validAssignments = assignments.filter(a => 
        (a.driver || (a.isManualDriver && a.manualDriverName)) && 
        (a.vehicle || (a.isManualVehicle && a.manualVehiclePlate) || a.manualVehiclePlate)
      );

      // Helper to convert undefined/empty to null
      const toNullIfEmpty = (val) => (val === undefined || val === '' || val === 'undefined') ? null : val;
      
      // Build assignments array for multi-truck support
      const assignmentsData = validAssignments.map((a, index) => {
        const driverName = a.driver 
          ? `${a.driver.firstName || ''} ${a.driver.lastName || ''}`.trim() || a.driver.nom
          : a.manualDriverName;
        const driverPhone = a.driver?.phone || a.driver?.telephone || a.manualDriverPhone;
        const driverId = a.driver?.id || null;
        
        const vehiclePlate = a.vehicle?.registration || a.vehicle?.plate || a.manualVehiclePlate;
        const vehicleType = a.vehicle?.vehicleType || a.vehicle?.type || a.manualVehicleType || operation?.vehicleType;
        const vehicleId = a.vehicle?.id || null;
        
        return {
          index: index,
          driverId: toNullIfEmpty(driverId),
          driverName: toNullIfEmpty(driverName),
          driverPhone: toNullIfEmpty(driverPhone),
          contactPhone: toNullIfEmpty(a.contactPhone) || null,
          driverSource: a.driver?.source || (a.isManualDriver ? 'manual' : 'local'),
          isManualDriver: a.isManualDriver || !a.driver,
          vehicleId: toNullIfEmpty(vehicleId),
          vehiclePlate: toNullIfEmpty(vehiclePlate),
          vehicleType: toNullIfEmpty(vehicleType),
          vehicleBrand: toNullIfEmpty(a.vehicle?.brand || a.manualVehicleBrand),
          vehicleModel: toNullIfEmpty(a.vehicle?.model || a.manualVehicleModel),
          vehicleCapacity: toNullIfEmpty(
            a.vehicle?.capacity != null ? String(a.vehicle.capacity) : a.manualVehicleCapacity
          ),
          vehicleSource: a.vehicle?.source || (a.isManualVehicle ? 'manual' : 'local'),
          isManualVehicle: a.isManualVehicle || !a.vehicle,
          hasTrailer: a.hasTrailer || false,
          trailerPlate: a.hasTrailer ? toNullIfEmpty(a.trailerPlate) : null,
          carrierPrice: Number(a.carrierPrice) || 0,
          partnerId: selectedPartner?.id || null,
          partnerName: selectedPartner?.name || null
        };
      });

      // For backward compatibility, use the first assignment for legacy fields
      const primaryAssignment = validAssignments[0] || assignments[0];
      
      const driverName = primaryAssignment.driver 
        ? `${primaryAssignment.driver.firstName || ''} ${primaryAssignment.driver.lastName || ''}`.trim() || primaryAssignment.driver.nom
        : primaryAssignment.manualDriverName;
        
      const driverPhone = primaryAssignment.driver?.phone || primaryAssignment.driver?.telephone || primaryAssignment.manualDriverPhone;
      
      const vehiclePlate = primaryAssignment.vehicle?.registration || primaryAssignment.vehicle?.plate || primaryAssignment.manualVehiclePlate;
      const vehicleType = primaryAssignment.vehicle?.vehicleType || primaryAssignment.vehicle?.type || primaryAssignment.manualVehicleType || operation?.vehicleType;

      // Assign local drivers
      for (const assignment of validAssignments) {
        if (assignment.driver && assignment.driver.source === 'local') {
          try {
            await axios.post(`${API}/resource-assignments/assign-driver`, {
              driverId: assignment.driver.id,
              operationId: operation.id
            }, { headers });
          } catch (e) {
            console.warn('Driver assignment warning:', e.response?.data?.detail);
          }
        }
        
        if (assignment.vehicle && assignment.vehicle.source === 'local') {
          try {
            await axios.post(`${API}/resource-assignments/assign-vehicle`, {
              vehicleId: assignment.vehicle.id,
              operationId: operation.id
            }, { headers });
          } catch (e) {
            console.warn('Vehicle assignment warning:', e.response?.data?.detail);
          }
        }
      }

      const logisticsData = {
        workflowStep: 'planifiee',
        // Multi-truck assignments array
        assignments: assignmentsData,
        // Legacy single assignment fields for backward compatibility
        vehicleType: toNullIfEmpty(vehicleType),
        vehiclePlate: toNullIfEmpty(vehiclePlate),
        vehicleBrand: toNullIfEmpty(primaryAssignment.vehicle?.brand || primaryAssignment.manualVehicleBrand),
        vehicleModel: toNullIfEmpty(primaryAssignment.vehicle?.model || primaryAssignment.manualVehicleModel),
        hasTrailer: primaryAssignment.hasTrailer || false,
        trailerPlate: primaryAssignment.hasTrailer ? toNullIfEmpty(primaryAssignment.trailerPlate) : null,
        driverName: toNullIfEmpty(driverName),
        driverPhone: toNullIfEmpty(driverPhone),
        driverId: toNullIfEmpty(primaryAssignment.driver?.id),
        carrierPriceTotal: assignmentsData.reduce((sum, a) => sum + (Number(a.carrierPrice) || 0), 0)
      };
      
      await axios.post(`${API}/operations/${operation.id}/logistics`, logisticsData, { headers });
      
      // Upload documents
      const uploadPromises = REQUIRED_DOCUMENTS.map(async (doc) => {
        const file = documentFiles[doc.type];
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', doc.type);
        formData.append('notes', `Document uploadé via planification`);
        
        return axios.post(`${API}/operations/${operation.id}/documents`, formData, {
          headers: { 'Authorization': 'Bearer cookie-auth', 'Content-Type': 'multipart/form-data' }
        });
      });
      
      await Promise.all(uploadPromises.filter(Boolean));
      
      // Change status if needed
      if (operation.status !== 'planifiee') {
        await axios.post(`${API}/operation-status/${operation.id}/change`, {
          new_status: 'planifiee',
          reason: `Planification complétée avec ${validAssignments.length}/${trucksRequested} camion(s) assigné(s)`,
          validate_requirements: false
        }, { headers });
      }
      
      toast({
        title: "✅ Planification complète",
        description: `${validAssignments.length}/${trucksRequested} camion(s) assigné(s) avec succès`
      });
      
      if (onSuccess) onSuccess();
      onClose();
      
    } catch (error) {
      console.error('Error submitting planification:', error);
      // Handle Pydantic validation errors (array of objects with type, loc, msg, input)
      let errorMessage = "Une erreur est survenue";
      const detail = error.response?.data?.detail;
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          errorMessage = detail.msg;
        }
      }
      toast({ title: "Erreur", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // Get source badge
  const getSourceBadge = (source) => {
    if (source === 'local') {
      return <Badge className="bg-emerald-100 text-emerald-800 border-0 text-xs"><Users className="w-3 h-3 mr-1" />Local</Badge>;
    }
    if (source === 'partner') {
      return <Badge className="bg-amber-100 text-amber-800 border-0 text-xs"><Handshake className="w-3 h-3 mr-1" />Partenaire</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-800 border-0 text-xs"><Database className="w-3 h-3 mr-1" />Truck DB</Badge>;
  };

  // Step 0: Partner Selection (Optional)
  const renderStep0 = () => {
    return (
      <div className="space-y-4" data-testid="partner-selection-step">
        <div className="flex items-center gap-2 mb-4">
          <Handshake className="w-5 h-5 text-[#1db584]" />
          <h3 className="text-lg font-semibold">Affecter un transporteur</h3>
          <Badge variant="outline" className="text-xs">Optionnel</Badge>
        </div>
        
        {/* Selected partner info */}
        {selectedPartner && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="selected-partner-info">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = PARTNER_TYPE_ICONS[selectedPartner.type] || Truck;
                  return <Icon className="w-5 h-5 text-amber-700" />;
                })()}
                <div>
                  <p className="font-semibold text-amber-900">{selectedPartner.name}</p>
                  <p className="text-sm text-amber-700">
                    {PARTNER_TYPE_LABELS[selectedPartner.type] || selectedPartner.type}
                    {' — '}
                    {selectedPartner.vehiclesCount || 0} vehicule(s), {selectedPartner.driversCount || 0} chauffeur(s)
                  </p>
                  {selectedPartner.coverageZones?.length > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      Zones : {selectedPartner.coverageZones.join(', ')}
                    </p>
                  )}
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedPartner(null)}
                className="text-amber-700 hover:text-red-600"
                data-testid="remove-partner-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Search input */}
        {!selectedPartner && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Rechercher par nom, téléphone…"
                value={partnerSearch}
                onChange={(e) => setPartnerSearch(e.target.value)}
                data-testid="partner-search-input"
              />
            </div>

            {/* Results */}
            <div className="max-h-[350px] overflow-y-auto space-y-2">
              {loadingPartners ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Recherche...
                </div>
              ) : partnerSearchResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Handshake className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>Aucun partenaire trouve</p>
                  <p className="text-xs mt-1">Vous pouvez passer cette étape</p>
                </div>
              ) : (
                partnerSearchResults.map(partner => {
                  const Icon = PARTNER_TYPE_ICONS[partner.type] || Truck;
                  const typeLabel = PARTNER_TYPE_LABELS[partner.type] || partner.type;
                  return (
                    <div
                      key={partner.id}
                      className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleSelectPartner(partner)}
                      data-testid={`partner-option-${partner.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">{partner.name}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Badge variant="outline" className="text-xs py-0">{typeLabel}</Badge>
                              <span>{partner.vehiclesCount || 0} vehicule(s)</span>
                              <span>{partner.driversCount || 0} chauffeur(s)</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-xs ${partner.availability === 'disponible' ? 'bg-emerald-100 text-emerald-800' : partner.availability === 'indisponible' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                            {partner.availability || 'inconnu'}
                          </Badge>
                          {partner.coverageZones?.length > 0 && (
                            <p className="text-xs text-gray-400 mt-1">{partner.coverageZones.slice(0, 2).join(', ')}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Fleet summary */}
                      {partner.fleet?.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {partner.fleet.map((f, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {f.quantity}x {f.vehicle_type_label || f.vehicle_type}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    );
  };



  // Step 1: Driver Selection (Multi-truck aware)
  const renderStep1 = () => {
    const filteredDrivers = getFilteredDrivers();
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Sélection des Chauffeurs</h3>
          </div>
          <Badge variant="outline" className="text-lg px-3 py-1">
            {trucksRequested} camion{trucksRequested > 1 ? 's' : ''} demandé{trucksRequested > 1 ? 's' : ''}
          </Badge>
        </div>
        
        {/* Search and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par nom, téléphone, matricule..."
              value={driverSearch}
              onChange={(e) => setDriverSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={driverSource} onValueChange={setDriverSource}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sources</SelectItem>
              {selectedPartner && <SelectItem value="partner">Partenaire</SelectItem>}
              <SelectItem value="local">Locaux</SelectItem>
              <SelectItem value="external">Truck DB</SelectItem>
            </SelectContent>
          </Select>
          {loadingExternal && <Loader2 className="w-5 h-5 animate-spin text-blue-500" />}
          {selectedPartner && (
            <Badge className="bg-amber-100 text-amber-800 border-0 text-xs">
              <Handshake className="w-3 h-3 mr-1" />{selectedPartner.name}
            </Badge>
          )}
        </div>

        {selectedPartner && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs text-amber-900" data-testid="driver-fleet-scope">
              {driverSource === 'partner'
                ? <>Flotte <strong>{selectedPartner.name}</strong> — {(selectedPartner.drivers || []).length} chauffeur(s)</>
                : <>Recherche élargie à toutes les sources</>}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs shrink-0"
              data-testid="driver-expand-search-btn"
              onClick={() => setDriverSource(driverSource === 'partner' ? 'all' : 'partner')}
            >
              {driverSource === 'partner' ? 'Élargir la recherche' : 'Revenir à ce transporteur'}
            </Button>
          </div>
        )}
        
        {/* Assignments Grid */}
        <div className="space-y-4">
          {assignments.map((assignment, idx) => (
            <div key={idx} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">
                  Camion #{idx + 1} {idx === 0 && <span className="text-xs text-gray-500">(Principal)</span>}
                </h4>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`manual-driver-${idx}`}
                    checked={assignment.isManualDriver}
                    onCheckedChange={(checked) => toggleManualDriver(idx, checked)}
                  />
                  <Label htmlFor={`manual-driver-${idx}`} className="text-sm cursor-pointer">
                    Saisie manuelle
                  </Label>
                </div>
              </div>
              
              {!assignment.isManualDriver ? (
                <>
                  {assignment.driver ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                          assignment.driver.source === 'local' ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}>
                          {(assignment.driver.firstName?.charAt(0) || assignment.driver.nom?.charAt(0) || 'C')}
                        </div>
                        <div>
                          <p className="font-medium">
                            {assignment.driver.firstName} {assignment.driver.lastName || assignment.driver.nom}
                          </p>
                          <p className="text-sm text-gray-600">{assignment.driver.phone || assignment.driver.telephone}</p>
                        </div>
                        {getSourceBadge(assignment.driver.source)}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeDriver(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <Select onValueChange={(value) => {
                      const driver = filteredDrivers.find(d => d.id === value);
                      if (driver) selectDriver(idx, driver);
                    }}>
                      <SelectTrigger data-testid={`driver-select-${idx}`}>
                        <SelectValue placeholder="Sélectionner un chauffeur..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {filteredDrivers.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-500">
                            {selectedPartner && driverSource === 'partner'
                              ? `Aucun chauffeur enregistré pour ${selectedPartner.name}. Cochez « Saisie manuelle » ou élargissez la recherche.`
                              : 'Aucun chauffeur disponible'}
                          </div>
                        ) : (
                          filteredDrivers.map(driver => (
                            <SelectItem key={driver.id} value={driver.id}>
                              <div className="flex items-center gap-2">
                                {driver.source === 'external' ? (
                                  <Database className="w-3 h-3 text-blue-500" />
                                ) : (
                                  <Users className="w-3 h-3 text-emerald-500" />
                                )}
                                <span>{driver.firstName} {driver.lastName || driver.nom}</span>
                                <span className="text-gray-400">-</span>
                                <span className="text-gray-500">{driver.phone || driver.telephone}</span>
                                {driver.matricule && <span className="text-xs text-gray-400">({driver.matricule})</span>}
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}

                </>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      placeholder="ex: Jean Kouassi"
                      value={assignment.manualDriverName}
                      onChange={(e) => updateAssignment(idx, 'manualDriverName', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input
                      placeholder="ex: +225 07 XX XX XX"
                      value={assignment.manualDriverPhone}
                      onChange={(e) => updateAssignment(idx, 'manualDriverPhone', e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Contact Opérationnel affiché au client */}
              <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
                <Label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  Numéro affiché au client
                </Label>
                <Input
                  placeholder="ex: +225 05 64 73 09 09"
                  value={assignment.contactPhone || ''}
                  onChange={(e) => updateAssignment(idx, 'contactPhone', e.target.value)}
                  className="text-sm"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Pré-rempli avec votre numéro. Le client ne verra jamais le numéro direct du chauffeur.
                </p>
              </div>
            </div>
          ))}
        </div>
        
        {/* Stats */}
        <div className="flex gap-4 text-sm text-gray-600 pt-2">
          <span>🟢 {localDrivers.length} locaux</span>
          <span>🔵 {externalDrivers.length} Truck DB</span>
          <span>✅ {assignments.filter(a => a.driver || a.manualDriverName).length}/{trucksRequested} assignés</span>
        </div>
      </div>
    );
  };

  // Step 2: Vehicle Selection (Multi-truck aware)
  const renderStep2 = () => {
    const filteredVehicles = getFilteredVehicles();
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Truck className="w-5 h-5 text-[#1db584]" />
            <h3 className="text-lg font-semibold">Sélection des Véhicules</h3>
          </div>
          <Badge variant="outline">
            Type demandé: {getVehicleTypeLabel(operation?.vehicleType) || operation?.vehicleType}
          </Badge>
        </div>
        
        {/* Search and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Rechercher par plaque, type..."
              value={vehicleSearch}
              onChange={(e) => setVehicleSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={vehicleSource} onValueChange={setVehicleSource}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes sources</SelectItem>
              {selectedPartner && <SelectItem value="partner">Partenaire</SelectItem>}
              <SelectItem value="local">Locaux</SelectItem>
              <SelectItem value="external">Truck DB</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selectedPartner && (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <p className="text-xs text-amber-900" data-testid="vehicle-fleet-scope">
              {vehicleSource === 'partner'
                ? <>Flotte <strong>{selectedPartner.name}</strong> — {(selectedPartner.vehicles || []).length} véhicule(s)</>
                : <>Recherche élargie à toutes les sources</>}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs shrink-0"
              data-testid="vehicle-expand-search-btn"
              onClick={() => setVehicleSource(vehicleSource === 'partner' ? 'all' : 'partner')}
            >
              {vehicleSource === 'partner' ? 'Élargir la recherche' : 'Revenir à ce transporteur'}
            </Button>
          </div>
        )}
        
        {/* Assignments Grid */}
        <div className="space-y-4">
          {assignments.map((assignment, idx) => (
            <div key={idx} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-700">Camion #{idx + 1}</h4>
                  {assignment.driver && (
                    <span className="text-sm text-gray-500">
                      ({assignment.driver.firstName} {assignment.driver.lastName || assignment.driver.nom || assignment.manualDriverName})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`manual-vehicle-${idx}`}
                    checked={assignment.isManualVehicle}
                    onCheckedChange={(checked) => toggleManualVehicle(idx, checked)}
                  />
                  <Label htmlFor={`manual-vehicle-${idx}`} className="text-sm cursor-pointer">
                    Saisie manuelle
                  </Label>
                </div>
              </div>
              
              {!assignment.isManualVehicle ? (
                <>
                  {assignment.vehicle ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getVehicleTypeIcon(assignment.vehicle.vehicleType || assignment.vehicle.type)}</span>
                        <div>
                          <p className="font-medium">{assignment.vehicle.registration || assignment.vehicle.plate}</p>
                          <p className="text-sm text-gray-600">
                            {assignment.vehicle.brand} {assignment.vehicle.model} • {assignment.vehicle.capacity}T
                          </p>
                        </div>
                        {getSourceBadge(assignment.vehicle.source)}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeVehicle(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ) : (
                    <Select onValueChange={(value) => {
                      const vehicle = filteredVehicles.find(v => v.id === value);
                      if (vehicle) selectVehicle(idx, vehicle);
                    }}>
                      <SelectTrigger data-testid={`vehicle-select-${idx}`}>
                        <SelectValue placeholder="Sélectionner un véhicule..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {filteredVehicles.length === 0 ? (
                          <div className="p-4 text-center text-sm text-gray-500">
                            {selectedPartner && vehicleSource === 'partner'
                              ? `Aucun véhicule enregistré pour ${selectedPartner.name}. Cochez « Saisie manuelle » ou élargissez la recherche.`
                              : 'Aucun véhicule disponible'}
                          </div>
                        ) : (
                          filteredVehicles.map(vehicle => (
                            <SelectItem key={vehicle.id} value={vehicle.id}>
                              <div className="flex items-center gap-2">
                                <span>{getVehicleTypeIcon(vehicle.vehicleType || vehicle.type)}</span>
                                {vehicle.source === 'external' ? (
                                  <Database className="w-3 h-3 text-blue-500" />
                                ) : (
                                  <Users className="w-3 h-3 text-emerald-500" />
                                )}
                                <span className="font-medium">{vehicle.registration || vehicle.plate}</span>
                                <span className="text-gray-400">-</span>
                                <span className="text-gray-500">{vehicle.capacity}T</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Plaque *</Label>
                      <Input
                        placeholder="ex: CI-1234-AB"
                        value={assignment.manualVehiclePlate}
                        onChange={(e) => updateAssignment(idx, 'manualVehiclePlate', e.target.value.toUpperCase())}
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select 
                        value={assignment.manualVehicleType} 
                        onValueChange={(v) => updateAssignment(idx, 'manualVehicleType', v)}
                      >
                        <SelectTrigger><SelectValue placeholder="Type..." /></SelectTrigger>
                        <SelectContent>
                          {VEHICLE_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Marque</Label>
                      <Input
                        placeholder="Mercedes"
                        value={assignment.manualVehicleBrand}
                        onChange={(e) => updateAssignment(idx, 'manualVehicleBrand', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Modèle</Label>
                      <Input
                        placeholder="Actros"
                        value={assignment.manualVehicleModel}
                        onChange={(e) => updateAssignment(idx, 'manualVehicleModel', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Capacité (T)</Label>
                      <Input
                        type="number"
                        placeholder="25"
                        value={assignment.manualVehicleCapacity}
                        onChange={(e) => updateAssignment(idx, 'manualVehicleCapacity', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={`trailer-${idx}`}
                      checked={assignment.hasTrailer}
                      onCheckedChange={(checked) => updateAssignment(idx, 'hasTrailer', checked)}
                    />
                    <Label htmlFor={`trailer-${idx}`}>Avec remorque</Label>
                    {assignment.hasTrailer && (
                      <Input
                        className="flex-1"
                        placeholder="Plaque remorque"
                        value={assignment.trailerPlate}
                        onChange={(e) => updateAssignment(idx, 'trailerPlate', e.target.value.toUpperCase())}
                      />
                    )}
                  </div>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <Label className="text-xs uppercase tracking-wider font-semibold text-gray-500">
                  Prix transporteur (FCFA)
                </Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="ex: 450000"
                  value={assignment.carrierPrice ?? ''}
                  onChange={(e) => updateAssignment(idx, 'carrierPrice', e.target.value)}
                  className="h-9 text-sm mt-1"
                  data-testid={`assignment-carrier-price-${idx}`}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Montant payé au transporteur pour ce camion — nécessaire pour connaître la marge réelle.
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Marge prévisionnelle (Lot A) */}
        {(() => {
          const revenue = Number(operation?.montantTotalHT || operation?.finalPrice || operation?.estimatedPrice || 0);
          const cost = assignments.reduce((sum, a) => sum + (Number(a.carrierPrice) || 0), 0);
          const missing = assignments.filter(a => !(Number(a.carrierPrice) > 0)).length;
          if (!revenue) return null;
          const margin = revenue - cost;
          const pct = revenue ? Math.round((margin / revenue) * 1000) / 10 : 0;
          const tone = missing > 0
            ? 'bg-gray-50 border-gray-200 text-gray-600'
            : margin < 0 ? 'bg-red-50 border-red-200 text-red-700'
            : pct < 14 ? 'bg-amber-50 border-amber-200 text-amber-700'
            : 'bg-emerald-50 border-emerald-200 text-emerald-700';
          return (
            <div className={`mt-3 rounded-lg border px-3 py-2 text-sm ${tone}`} data-testid="planification-margin-preview">
              {missing > 0 ? (
                <>Marge inconnue — prix transporteur manquant sur {missing} camion(s).</>
              ) : (
                <>Marge prévisionnelle : <strong>{margin.toLocaleString('fr-FR')} FCFA</strong> ({pct} %)
                  {margin < 0 && ' — opération vendue à perte'}</>
              )}
            </div>
          );
        })()}

        {/* Stats */}
        <div className="flex gap-4 text-sm text-gray-600 pt-2">
          <span>🟢 {localVehicles.length} locaux</span>
          <span>🔵 {externalVehicles.length} Truck DB</span>
          <span>✅ {assignments.filter(a => a.vehicle || a.manualVehiclePlate).length}/{trucksRequested} assignés</span>
        </div>
      </div>
    );
  };

  // Step 3: Documents
  const renderStep3 = () => {
    const validAssignments = assignments.filter(a => 
      (a.driver || a.manualDriverName) && (a.vehicle || a.manualVehiclePlate)
    );
    
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Upload className="w-5 h-5 text-[#1db584]" />
          <h3 className="text-lg font-semibold">Documents Justificatifs</h3>
        </div>
        
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Les documents sont <strong>optionnels</strong> pour la planification initiale.
          </AlertDescription>
        </Alert>
        
        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-3">
            Resume de la planification ({validAssignments.length}/{trucksRequested} camion{trucksRequested > 1 ? 's' : ''})
          </h4>
          {selectedPartner && (
            <div className="flex items-center gap-2 mb-3 text-sm text-amber-800 bg-amber-50 p-2 rounded">
              <Handshake className="w-4 h-4" />
              <span>Transporteur : <strong>{selectedPartner.name}</strong></span>
              <Badge variant="outline" className="text-xs">{PARTNER_TYPE_LABELS[selectedPartner.type] || selectedPartner.type}</Badge>
            </div>
          )}
          <div className="space-y-3">
            {validAssignments.map((a, idx) => (
              <div key={idx} className="flex items-center gap-4 text-sm p-2 bg-white rounded">
                <span className="font-medium">#{idx + 1}</span>
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4 text-gray-400" />
                  <span>{a.driver ? `${a.driver.firstName} ${a.driver.lastName || a.driver.nom}` : a.manualDriverName}</span>
                  {a.driver && getSourceBadge(a.driver.source)}
                </div>
                <div className="flex items-center gap-1">
                  <Truck className="w-4 h-4 text-gray-400" />
                  <span>{a.vehicle?.registration || a.vehicle?.plate || a.manualVehiclePlate}</span>
                  {a.vehicle && getSourceBadge(a.vehicle.source)}
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Documents upload */}
        <div className="space-y-3">
          {REQUIRED_DOCUMENTS.map((doc) => (
            <div key={doc.type} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor={doc.type} className="flex items-center gap-2">
                  <doc.icon className="w-4 h-4" />
                  {doc.label}
                </Label>
                {uploadedDocuments[doc.type] && <CheckCircle2 className="w-5 h-5 text-green-600" />}
              </div>
              <Input
                id={doc.type}
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange(doc.type, e.target.files[0])}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Planification de l'opération
            {trucksRequested > 1 && (
              <Badge className="bg-purple-100 text-purple-800">
                {trucksRequested} camions demandés
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Etape {step + 1} sur 4 : {step === 0 ? 'Transporteur' : step === 1 ? 'Chauffeurs' : step === 2 ? 'Vehicules' : 'Documents'}
          </DialogDescription>
        </DialogHeader>
        
        {/* Progress bar */}
        <div className="flex gap-2 mb-4">
          {[0, 1, 2, 3].map((s) => (
            <div key={s} className={`h-2 flex-1 rounded ${s <= step ? 'bg-[#1db584]' : 'bg-gray-200'}`} />
          ))}
        </div>
        
        {/* Content */}
        <div className="min-h-[400px]">
          {step === 0 && renderStep0()}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
        
        {/* Actions */}
        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={step === 0 ? onClose : handleBack} disabled={loading}>
            {step === 0 ? 'Annuler' : 'Precedent'}
          </Button>
          
          <div className="flex gap-2">
            {step === 0 && !selectedPartner && (
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="skip-partner-btn">
                Passer sans partenaire
              </Button>
            )}
            {step < 3 ? (
              <Button onClick={handleNext} disabled={loading} data-testid="next-step-btn">Suivant</Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} data-testid="validate-planification-btn">
                {loading ? 'Traitement...' : 'Valider la planification'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanificationModal;
